import React, { useEffect } from 'react';

import { Box, Typography } from '@mui/material';
import { useFormik } from 'formik';

import {
  IBookingsRequested,
  ICalendarEventType,
  bookingsRequested,
  getBookingRequested,
} from '@pharmaplan/common';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import {
  renderScreen,
  setBack,
  setButtons,
  setDrawerContentPadding,
  setFooterShow,
  setPagination,
  setTitle,
} from '../../../actions/drawerActions';
import strings from '../../../localization';
import { ScreenTypes } from '../../../helpers/Constants';

import CustomDropdown from '../../common/CustomDropdown/CustomDropdown';
import ViewOtherRequestOffers from '../ViewOtherRequestOffers';
import { setDialog } from '../../../reducers/dialogReducer';
import { getDayMonthDateYear } from '../../Reports/helpers';

import BookingRequest from './BookingRequest/BookingRequest';
import sorter, { sortTypes } from './utils';
import classes from './styles';

export interface IBookingRequestsList {
  eventId: string;
  date: string;
  type: ICalendarEventType;
  eventIds: Array<string>;
  preventInteraction?: boolean;
  longTermShiftContractId?: string;
}

const BookingRequestsList: React.FC<IBookingRequestsList> = ({
  eventId,
  date,
  type,
  eventIds,
  preventInteraction,
  longTermShiftContractId,
}) => {
  const dispatch = useAppDispatch();

  const events = useAppSelector(bookingsRequested);
  const pIds = events?.map((item) =>
    item.professionalId ?? '');

  const formik = useFormik({
    initialValues: {
      requestsList: 'requestedDate',
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const tempEvents = events.slice();
  const { sortByDate, sortByBoolean, sortByNumeric } = sorter(tempEvents);

  const sortBy = [
    { key: 'requestedDate', label: strings.requestedDate },
    { key: 'rating', label: strings.rating },
    { key: 'price', label: strings.totalCost },
    { key: 'distance', label: strings.distance },
    { key: 'isFavorite', label: strings.favourite },
  ];

  const sortFunctions = () =>
    ({
      requestedDate: () =>
        sortByDate('requestedDate'),
      rating: () =>
        sortByNumeric('rating', sortTypes.descending),
      price: () =>
        sortByNumeric('price', sortTypes.ascending),
      distance: () =>
        sortByNumeric('distance', sortTypes.ascending),
      isFavorite: () =>
        sortByBoolean('isFavorite'),
    });

  const sortedEvents = (
    sortFunctions()[
      formik.values.requestsList as keyof typeof sortFunctions
    ] as () => Array<IBookingsRequested>
  )();

  const gotoWorkshiftSummary = () => {
    dispatch(
      renderScreen({
        eventIds,
        type,
        date,
        screenNumber: 5,
        screenType: ScreenTypes.workshiftSummary,
      }),
    );
  };

  const openOtherOffers = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.otherOffersForShift,
        },
        Component: (
          <ViewOtherRequestOffers
            professionalIds={pIds}
            workshiftId={eventId}
          />
        ),
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  React.useEffect(() => {
    dispatch(getBookingRequested(eventId));

    dispatch(
      setTitle({
        title: strings.formatString(
          strings.workshiftDate,
          getDayMonthDateYear(date),
        ) as string,
        customTitleStyle: classes.header,
      }),
    );

    dispatch(setBack(gotoWorkshiftSummary));
    dispatch(setDrawerContentPadding('0px'));

    return () => {
      dispatch(setFooterShow(true));
      dispatch(setBack(null));
      dispatch(setDrawerContentPadding('16px'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (longTermShiftContractId && events[0]?.showOtherRequests) {
      dispatch(setFooterShow(true));
      dispatch(setPagination({ show: false }));
      dispatch(
        setButtons({
          primaryButton: {
            label: null,
            onClick: null,
          },
          secondaryButton: {
            label: strings.viewOtherOffersForShift,
            onClick: openOtherOffers,
          },
          show: true,
        }),
      );
    } else {
      dispatch(setFooterShow(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  return (
    <>
      <Box sx={classes.root}>
        <Box sx={classes.container}>
          <Typography sx={classes.title}>
            {strings.formatString(strings.bookingRequestsNum, events.length)}
          </Typography>
        </Box>

        <CustomDropdown
          label={`${strings.sortBy}: `}
          horizontal
          errorContainer={false}
          customClass={classes.filterDropdown}
          formik={formik}
          id="requestsListFilter"
          name="requestsList"
          menuItems={sortBy}
        />
      </Box>

      {sortedEvents.map((item: IBookingsRequested) =>
        (
          <BookingRequest
            key={item.professionalId ?? item.bookingId}
            date={date}
            eventId={eventId}
            eventIds={eventIds}
            type={type}
            event={item}
            preventInteraction={preventInteraction}
          />
        ))}
    </>
  );
};

export default BookingRequestsList;
